import React, { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, UserRole } from '../../contexts/AuthContext';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { EXTERNAL_LOGIN_DENIED_MESSAGE } from '../../lib/internalLogin';

interface RequireRoleProps {
  children: ReactNode;
  roles: UserRole | UserRole[];
  fallbackPath?: string;
  showMessage?: boolean;
}

/**
 * RequireRole 組件
 * 用於保護路由和組件，確保只有特定角色可以存取
 * 
 * @example
 * // 只允許管理員存取
 * <RequireRole roles="admin">
 *   <AdminPage />
 * </RequireRole>
 * 
 * @example
 * // 允許管理員和營運人員存取
 * <RequireRole roles={['admin', 'operator']}>
 *   <ManagementPage />
 * </RequireRole>
 */
const RequireRole: React.FC<RequireRoleProps> = ({ 
  children, 
  roles, 
  fallbackPath = '/dashboard',
  showMessage = true 
}) => {
  const { user, userRole, loading, authInitialized, hasRole } = useAuth();

  // 載入中顯示載入畫面
  if (loading || !authInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">載入中...</p>
        </div>
      </div>
    );
  }

  // 未登入，導向登入頁
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 沒有角色資訊，顯示等待授權頁面
  if (!userRole) {
    return showMessage ? <NoRoleMessage /> : <Navigate to={fallbackPath} replace />;
  }

  // 檢查角色權限
  if (!hasRole(roles)) {
    return showMessage ? (
      <AccessDeniedMessage requiredRoles={roles} currentRole={userRole.role} />
    ) : (
      <Navigate to={fallbackPath} replace />
    );
  }

  // 權限通過，渲染子組件
  return <>{children}</>;
};

/**
 * 沒有角色資訊的提示頁面
 */
const NoRoleMessage: React.FC = () => {
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('登出失敗:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
            <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            無法登入
          </h2>
          
          <p className="text-gray-600 mb-6">
            {EXTERNAL_LOGIN_DENIED_MESSAGE}。您的帳號 <span className="font-semibold text-gray-900">{user?.email}</span> 沒有內部存取權限。
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 w-full">
            <p className="text-sm text-blue-800">
              若您是內部人員，請聯絡系統管理員為您開通營運人員或系統管理員角色。
            </p>
          </div>
          
          <button
            onClick={handleSignOut}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            登出
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * 權限不足的提示頁面
 */
interface AccessDeniedMessageProps {
  requiredRoles: UserRole | UserRole[];
  currentRole: UserRole;
}

const AccessDeniedMessage: React.FC<AccessDeniedMessageProps> = ({ requiredRoles, currentRole }) => {
  const roleNames: Record<UserRole, string> = {
    admin: '系統管理員',
    operator: '營運人員',
    customer: '客戶',
    partner: '承攬夥伴'
  };

  const requiredRoleNames = (Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles])
    .map(role => roleNames[role])
    .join(' 或 ');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            權限不足
          </h2>
          
          <p className="text-gray-600 mb-4">
            此功能僅限 <span className="font-semibold text-gray-900">{requiredRoleNames}</span> 使用。
          </p>
          
          <p className="text-sm text-gray-500 mb-6">
            您目前的角色：<span className="font-semibold">{roleNames[currentRole]}</span>
          </p>
          
          <button
            onClick={() => window.history.back()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            返回上一頁
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequireRole;

